import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUserTienda } from "../../services/LoginUserTienda";

function Login() {
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [btnHover, setBtnHover] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const resultado = await loginUserTienda(usuario, password);

    if (!resultado?.ok) {
      localStorage.removeItem("usuario_token");
      localStorage.removeItem("usuario_data");
      localStorage.removeItem("usuario_permisos");
      setError(resultado?.data?.message || "Credenciales inválidas.");
      return;
    }

    const token =
      resultado?.data?.token ||
      resultado?.data?.access_token ||
      resultado?.data?.jwt ||
      resultado?.data?.data?.token;

    if (!token) {
      localStorage.removeItem("usuario_token");
      localStorage.removeItem("usuario_data");
      localStorage.removeItem("usuario_permisos");
      setError("No se recibió token del backend.");
      return;
    }

    const usuarioData = resultado?.data?.usuario || null;
    const permisos = resultado?.data?.permisos || [];

    localStorage.setItem("usuario_token", token);
    localStorage.setItem("newSession", "1");

    if (usuarioData) {
      localStorage.setItem("usuario_data", JSON.stringify(usuarioData));
    }

    localStorage.setItem("usuario_permisos", JSON.stringify(permisos));

    navigate("/administrator/admin");
  };

  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 640 : false;

  const theme = {
    bgTop: "#0B1220",
    bgMid: "#0F172A",
    bgBottom: "#020617",

    card: "rgba(15, 23, 42, 0.82)",
    card2: "rgba(255,255,255,0.04)",
    card3: "rgba(255,255,255,0.06)",

    border: "rgba(148, 163, 184, 0.18)",
    borderStrong: "rgba(59, 130, 246, 0.28)",

    primary: "#2563EB",
    primaryHover: "#1D4ED8",
    accent: "#0EA5E9",

    text: "#F8FAFC",
    muted: "rgba(226, 232, 240, 0.78)",
    muted2: "rgba(148, 163, 184, 0.78)",

    inputBg: "rgba(255,255,255,0.045)",
    errorBg: "rgba(239, 68, 68, 0.10)",
    errorBorder: "rgba(248, 113, 113, 0.28)",
    errorText: "#FCA5A5",
  };

  const styles = {
    page: {
      minHeight: "100dvh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: isMobile ? 16 : 20,
      position: "relative",
      overflow: "hidden",
      background: `
        radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%),
        radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.14), transparent 30%),
        linear-gradient(180deg, ${theme.bgTop} 0%, ${theme.bgMid} 50%, ${theme.bgBottom} 100%)
      `,
      boxSizing: "border-box",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },

    gridGlow: {
      position: "absolute",
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
      `,
      backgroundSize: isMobile ? "28px 28px" : "36px 36px",
      maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.2))",
      pointerEvents: "none",
      zIndex: 0,
    },

    glow: {
      position: "absolute",
      width: isMobile ? 320 : 520,
      height: isMobile ? 320 : 520,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      background:
        "radial-gradient(circle, rgba(37,99,235,0.18) 0%, rgba(14,165,233,0.10) 42%, transparent 72%)",
      filter: isMobile ? "blur(70px)" : "blur(90px)",
      pointerEvents: "none",
      zIndex: 0,
    },

    topBadge: {
      position: "absolute",
      top: isMobile ? 18 : 30,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2,
      padding: isMobile ? "8px 14px" : "10px 18px",
      borderRadius: 999,
      background: "rgba(15, 23, 42, 0.65)",
      border: `1px solid ${theme.border}`,
      color: theme.muted,
      fontSize: isMobile ? 10 : 12,
      fontWeight: 800,
      letterSpacing: isMobile ? "1px" : "1.6px",
      textTransform: "uppercase",
      backdropFilter: "blur(12px)",
      boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
      whiteSpace: "nowrap",
    },

    card: {
      width: "100%",
      maxWidth: isMobile ? "100%" : "460px",
      padding: isMobile ? "24px 18px" : "34px 32px",
      borderRadius: isMobile ? 20 : 24,
      background: `
        linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015)),
        ${theme.card}
      `,
      border: `1px solid ${theme.border}`,
      boxShadow: isMobile
        ? "0 18px 40px rgba(0,0,0,0.35)"
        : "0 30px 80px rgba(0,0,0,0.45)",
      color: theme.text,
      backdropFilter: "blur(20px)",
      zIndex: 2,
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
      marginTop: isMobile ? 38 : 24,
    },

    cardTopLine: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: 3,
      background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`,
    },

    logoWrap: {
      width: isMobile ? 64 : 74,
      height: isMobile ? 64 : 74,
      margin: "0 auto 18px auto",
      borderRadius: isMobile ? 16 : 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(145deg, rgba(37,99,235,0.18), rgba(14,165,233,0.10))",
      border: `1px solid ${theme.borderStrong}`,
      boxShadow: "0 16px 40px rgba(37,99,235,0.18)",
    },

    logo: {
      width: isMobile ? 36 : 42,
      height: isMobile ? 36 : 42,
      objectFit: "contain",
      opacity: 0.95,
    },

    title: {
      textAlign: "center",
      margin: 0,
      fontSize: isMobile ? 23 : 28,
      fontWeight: 900,
      letterSpacing: "-0.5px",
      color: theme.text,
      lineHeight: 1.15,
    },

    subtitle: {
      marginTop: 8,
      marginBottom: 6,
      textAlign: "center",
      fontSize: isMobile ? 12 : 13,
      color: theme.accent,
      fontWeight: 800,
      letterSpacing: "0.8px",
      textTransform: "uppercase",
    },

    description: {
      textAlign: "center",
      margin: "0 0 24px 0",
      fontSize: isMobile ? 13 : 14,
      color: theme.muted2,
      lineHeight: 1.5,
    },

    error: {
      background: theme.errorBg,
      color: theme.errorText,
      padding: "12px 14px",
      borderRadius: 14,
      marginBottom: 16,
      border: `1px solid ${theme.errorBorder}`,
      fontSize: 13,
      fontWeight: 700,
    },

    fieldGroup: {
      marginBottom: 16,
    },

    label: {
      display: "block",
      marginBottom: 8,
      fontSize: 13,
      fontWeight: 800,
      color: theme.muted,
      letterSpacing: "0.2px",
    },

    inputWrap: {
      position: "relative",
    },

    inputBar: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      width: 10,
      height: 10,
      borderRadius: 999,
      background: "linear-gradient(135deg, #2563EB, #0EA5E9)",
      boxShadow: "0 0 12px rgba(37,99,235,0.35)",
      pointerEvents: "none",
    },

    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "14px 14px 14px 36px",
      borderRadius: 14,
      border: `1px solid ${theme.border}`,
      background: theme.inputBg,
      color: theme.text,
      fontSize: isMobile ? 13 : 14,
      outline: "none",
      transition: "all 0.18s ease",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    },

    btn: {
      width: "100%",
      padding: isMobile ? "13px 14px" : "14px 16px",
      marginTop: 8,
      border: "none",
      borderRadius: 14,
      cursor: "pointer",
      fontWeight: 900,
      fontSize: isMobile ? 13 : 14,
      letterSpacing: "0.8px",
      color: "#fff",
      background: btnHover
        ? `linear-gradient(135deg, ${theme.primaryHover}, ${theme.accent})`
        : `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
      boxShadow: btnHover
        ? "0 18px 38px rgba(37,99,235,0.30)"
        : "0 14px 30px rgba(37,99,235,0.22)",
      transform: btnHover ? "translateY(-1px)" : "translateY(0)",
      transition: "all 0.18s ease",
    },

    footer: {
      marginTop: 18,
      paddingTop: 16,
      borderTop: "1px solid rgba(255,255,255,0.06)",
      textAlign: "center",
    },

    footerText: {
      fontSize: isMobile ? 11 : 12,
      color: theme.muted2,
      fontWeight: 600,
      lineHeight: 1.5,
    },

    footerBrand: {
      marginTop: 6,
      fontSize: isMobile ? 11 : 12,
      color: theme.accent,
      fontWeight: 900,
      letterSpacing: "0.6px",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.gridGlow} />
      <div style={styles.glow} />

      <div style={styles.topBadge}>Panel administrativo</div>

      <form onSubmit={handleLogin} style={styles.card}>
        <div style={styles.cardTopLine} />

        <div style={styles.logoWrap}>
          <img src="/image/NB.png" alt="Logo NB" style={styles.logo} />
        </div>

        <h2 style={styles.title}>Acceso Administrador</h2>
        <div style={styles.subtitle}>NB Velora</div>
        <p style={styles.description}>
          Ingresa con tus credenciales.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Usuario administrador</label>
          <div style={styles.inputWrap}>
            <span style={styles.inputBar} />
            <input
              type="text"
              required
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              style={styles.input}
              placeholder="Ingresa tu usuario"
              onFocus={(e) => {
                e.target.style.borderColor = theme.borderStrong;
                e.target.style.background = theme.card3;
                e.target.style.boxShadow = "0 0 0 4px rgba(37,99,235,0.14)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.border;
                e.target.style.background = theme.inputBg;
                e.target.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.03)";
              }}
            />
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Contraseña</label>
          <div style={styles.inputWrap}>
            <span style={styles.inputBar} />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Ingresa tu contraseña"
              onFocus={(e) => {
                e.target.style.borderColor = theme.borderStrong;
                e.target.style.background = theme.card3;
                e.target.style.boxShadow = "0 0 0 4px rgba(37,99,235,0.14)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.border;
                e.target.style.background = theme.inputBg;
                e.target.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.03)";
              }}
            />
          </div>
        </div>

        <button
          type="submit"
          style={styles.btn}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
        >
          Ingresar al panel
        </button>

        <div style={styles.footer}>
          <div style={styles.footerText}>
            Acceso restringido · Uso exclusivo de personal autorizado
          </div>
          <div style={styles.footerBrand}>Sistema Administrativo NB</div>
        </div>
      </form>
    </div>
  );
}

export default Login;